/**
 * 图标注册表 — 语义 → lucide-react 组件的唯一落地入口。
 *
 * 语义判定权威：docs/06-ICON-MAP.md（§2 业务对象 / §3 状态 8 态 / §4 反馈三档 / §5 导航 / §6 动作 / §8 空状态）。
 * 本文件只做「名字 → 组件」的机械映射，**不得自行改义**（06 §11）。
 *
 * P0 约束：
 * - 全项目只允许从这里取图标；禁止在业务组件里直接 import lucide 具名组件（便于一处审计）。
 * - 禁止 emoji 作为功能图标；禁止引入第二套图标库（02-ARCHITECTURE.md §2.7）。
 */
import {
  Activity,
  Archive,
  ArrowDown,
  ArrowLeftRight,
  ArrowUp,
  ArrowUpRight,
  Ban,
  Boxes,
  CalendarPlus,
  Check,
  ChevronDown,
  ChevronRight,
  Circle,
  CircleAlert,
  CircleCheck,
  CircleDot,
  CirclePause,
  Clock,
  ClockAlert,
  Contact,
  Copy,
  Download,
  Ellipsis,
  ExternalLink,
  Eye,
  EyeOff,
  FileDown,
  FileKey,
  FilterX,
  History,
  Info,
  KeyRound,
  Layers,
  LayoutDashboard,
  ListFilter,
  LoaderCircle,
  LogOut,
  Menu,
  Monitor,
  MonitorSmartphone,
  Moon,
  OctagonX,
  Package,
  Pause,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  Repeat,
  Replace,
  ScanBarcode,
  ScrollText,
  Search,
  SearchX,
  Settings2,
  ShieldCheck,
  SlidersHorizontal,
  Sun,
  Terminal,
  Trash,
  TriangleAlert,
  Undo2,
  Unlock,
  Unplug,
  UserCog,
  UserX,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export const ICON_REGISTRY = {
  /* ── §3 状态 8 态（LOCKED，形状互异） ── */
  'circle': Circle,
  'arrow-up-right': ArrowUpRight,
  'circle-check': CircleCheck,
  'ban': Ban,
  'circle-dot': CircleDot,
  'circle-pause': CirclePause,
  'clock': Clock,
  'octagon-x': OctagonX,
  'clock-alert': ClockAlert,

  /* ── §4 反馈（三档告警层级：triangle-alert < circle-alert < octagon-x） ── */
  'triangle-alert': TriangleAlert,
  'circle-alert': CircleAlert,
  'unlock': Unlock,
  'info': Info,
  'loader-circle': LoaderCircle,
  'shield-check': ShieldCheck,

  /* ── §5 导航（20px） ── */
  'layout-dashboard': LayoutDashboard,
  'package': Package,
  'layers': Layers,
  'boxes': Boxes,
  'scan-barcode': ScanBarcode,
  'file-key': FileKey,
  'monitor-smartphone': MonitorSmartphone,
  'contact': Contact,
  'key-round': KeyRound,
  'user-cog': UserCog,
  'terminal': Terminal,
  'scroll-text': ScrollText,
  'activity': Activity,
  'sliders-horizontal': SlidersHorizontal,
  'repeat': Repeat,
  'monitor': Monitor,

  /* ── §6 动作 ── */
  'plus': Plus,
  'pencil': Pencil,
  'copy': Copy,
  'check': Check,
  'download': Download,
  'file-down': FileDown,
  'search': Search,
  'list-filter': ListFilter,
  'settings-2': Settings2,
  'refresh-cw': RefreshCw,
  'undo-2': Undo2,
  'pause': Pause,
  'play': Play,
  'calendar-plus': CalendarPlus,
  'unplug': Unplug,
  'arrow-left-right': ArrowLeftRight,
  'replace': Replace,
  'trash': Trash,
  'ellipsis': Ellipsis,
  'chevron-right': ChevronRight,
  'chevron-down': ChevronDown,
  'external-link': ExternalLink,
  'user-x': UserX,
  'archive': Archive,
  'history': History,
  'eye': Eye,
  'eye-off': EyeOff,
  /* 表格排序指示（03-UIUX.md §6.4「当前排序列显示 arrow-up / arrow-down 图标」） */
  'arrow-up': ArrowUp,
  'arrow-down': ArrowDown,

  /* ── §8 空状态 ── */
  'search-x': SearchX,
  'filter-x': FilterX,

  /* ── 界面外壳（非业务语义，06 未分配；已在交付说明中登记待设计确认） ──
     这几项是「侧边栏抽屉开关 / 关闭 / 主题切换 / 退出」这类壳层控件所必需，
     06-ICON-MAP.md §5–§6 未覆盖。登记在此处而非散落在页面里，
     是为了设计师回复后只需改这一处。                                       */
  'menu': Menu,
  'x': X,
  'sun': Sun,
  'moon': Moon,
  'log-out': LogOut,
} as const satisfies Record<string, LucideIcon>;

export type IconName = keyof typeof ICON_REGISTRY;

export function resolveIcon(name: IconName): LucideIcon {
  return ICON_REGISTRY[name];
}
